/**
 * Customer-facing PUBLIC payment result page.
 * URL: /payment/success?session_id={CHECKOUT_SESSION_ID}
 * Polls /api/payments/status/{session_id} every 2s until we land in a terminal status.
 * Handles all 7 canonical statuses:
 *   pending / processing → keep polling (max ~30s)
 *   paid                 → success confirmation
 *   failed / expired / cancelled → error UX with retry CTA (only when payment_link still valid)
 *   refunded             → refund confirmation
 */
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, RefreshCcw, Home, Copy, AlertTriangle, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { floorAED } from '../utils/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // 60 seconds
const TERMINAL = new Set(['paid', 'failed', 'expired', 'cancelled', 'refunded']);

const STATUS_UI = {
  pending: {
    icon: Clock, color: '#eab308', bg: 'bg-yellow-500/15',
    titleAr: 'بانتظار الدفع', titleEn: 'Awaiting Payment',
    descAr: 'جاري التحقق من حالة الدفعة...', descEn: 'Verifying your payment...',
  },
  processing: {
    icon: RefreshCcw, color: '#3b82f6', bg: 'bg-blue-500/15',
    titleAr: 'قيد المعالجة', titleEn: 'Processing',
    descAr: 'يقوم البنك بمعالجة الدفعة، لحظات...', descEn: 'The bank is processing your payment...',
  },
  paid: {
    icon: CheckCircle2, color: '#10b981', bg: 'bg-emerald-500/15',
    titleAr: 'تم الدفع بنجاح', titleEn: 'Payment Successful',
    descAr: 'شكراً لك، تم استلام دفعتك بنجاح.', descEn: 'Thank you! Your payment has been received.',
  },
  failed: {
    icon: XCircle, color: '#ef4444', bg: 'bg-red-500/15',
    titleAr: 'فشلت الدفعة', titleEn: 'Payment Failed',
    descAr: 'لم نتمكن من إتمام عملية الدفع.', descEn: 'We could not complete your payment.',
  },
  expired: {
    icon: AlertTriangle, color: '#f97316', bg: 'bg-orange-500/15',
    titleAr: 'انتهت صلاحية الرابط', titleEn: 'Link Expired',
    descAr: 'رابط الدفع لم يعد صالحاً. تواصل معنا لإصدار رابط جديد.', descEn: 'This payment link is no longer valid. Please request a new one.',
  },
  cancelled: {
    icon: XCircle, color: '#71717a', bg: 'bg-slate-500/15',
    titleAr: 'تم إلغاء الدفع', titleEn: 'Payment Cancelled',
    descAr: 'تم إلغاء عملية الدفع من طرفك.', descEn: 'You cancelled the payment.',
  },
  refunded: {
    icon: RotateCcw, color: '#8b5cf6', bg: 'bg-violet-500/15',
    titleAr: 'تم استرداد الدفعة', titleEn: 'Refunded',
    descAr: 'تم استرداد المبلغ إلى وسيلة الدفع الأصلية.', descEn: 'The amount has been refunded to your original payment method.',
  },
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const invoiceRef = params.get('inv');
  const linkRef = params.get('link_id');
  const paymentIntentId = params.get('payment_intent_id');
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const isAr = (typeof document !== 'undefined' && document.documentElement.lang === 'ar') || true;

  // ─── Non-Stripe callbacks (Ziina / Tamara / Tabby via `inv=` or `link_id=`)
  //    → immediately hand off to the branded receipt page which handles all
  //      four providers uniformly and reconciles status server-side.
  useEffect(() => {
    if (sessionId) return;                                        // Stripe path handled below
    const anchor = invoiceRef || linkRef;
    if (!anchor) return;
    const qs = new URLSearchParams({ state: 'success' });
    if (paymentIntentId) qs.set('payment_intent_id', paymentIntentId);
    // Same-origin SPA navigation — PayReceiptPage handles polling + status reconciliation.
    navigate(`/pay/receipt/${encodeURIComponent(anchor)}?${qs.toString()}`, { replace: true });
  }, [sessionId, invoiceRef, linkRef, paymentIntentId, navigate]);

  const fetchStatus = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
      setData(res.data);
      if (TERMINAL.has(res.data.payment_status)) {
        clearTimeout(timerRef.current);
        return;
      }
    } catch (e) {
      // 404 or transient — count as a failed attempt but keep polling
    }
    setAttempts((n) => n + 1);
  };

  useEffect(() => {
    if (!sessionId) return;
    fetchStatus();
  }, [sessionId]);

  useEffect(() => {
    if (!data) return;
    if (TERMINAL.has(data.payment_status)) return;
    if (attempts >= MAX_ATTEMPTS) {
      setPollExhausted(true);
      return;
    }
    timerRef.current = setTimeout(fetchStatus, POLL_INTERVAL_MS);
    return () => clearTimeout(timerRef.current);
  }, [data, attempts]);

  // Only reach this "invalid link" branch when there's NO Stripe session AND
  // no invoice/link reference either.
  if (!sessionId && !invoiceRef && !linkRef) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="payment-page-missing-session">
            {isAr ? 'رابط غير صالح' : 'Invalid Link'}
          </h1>
          <p className="text-white/60 mb-6">{isAr ? 'معرف الدفع مفقود من الرابط.' : 'The payment identifier is missing from the URL.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl bg-[#d4af37] text-black font-bold hover:bg-[#d4af37]/90 transition"
            data-testid="payment-page-home-btn"
          >
            {isAr ? 'الصفحة الرئيسية' : 'Home'}
          </button>
        </div>
      </div>
    );
  }

  // Waiting for the auto-redirect (Ziina/Tamara/Tabby via inv= / link_id=)
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4">
        <div className="text-center">
          <RefreshCcw className="w-12 h-12 text-[#d4af37] mx-auto mb-4 animate-spin" />
          <p className="text-white/70">{isAr ? 'جارٍ تحميل الإيصال...' : 'Loading receipt...'}</p>
        </div>
      </div>
    );
  }

  const status = data?.payment_status || 'pending';
  const ui = STATUS_UI[status] || STATUS_UI.pending;
  const Icon = ui.icon;
  const showRetry = ['failed', 'cancelled'].includes(status) && data?.payment_link;

  const copySession = async () => {
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4 py-10" dir={isAr ? 'rtl' : 'ltr'}>
      {/* soft radial background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-[140px]"
          style={{ background: `${ui.color}22` }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-lg bg-white/[0.03] backdrop-blur-2xl border rounded-3xl p-8 sm:p-10 text-center"
        style={{ borderColor: `${ui.color}55` }}
        data-testid="payment-result-card"
      >
        {/* Status icon */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 220 }}
          className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${ui.bg}`}
          style={{ border: `2px solid ${ui.color}` }}
        >
          <Icon className="w-10 h-10" style={{ color: ui.color }} />
        </motion.div>

        <h1
          className="text-3xl sm:text-4xl font-black text-white mb-3"
          data-testid={`payment-status-title-${status}`}
        >
          {isAr ? ui.titleAr : ui.titleEn}
        </h1>
        <p className="text-white/70 mb-8 text-sm sm:text-base leading-relaxed" data-testid="payment-status-desc">
          {isAr ? ui.descAr : ui.descEn}
        </p>

        {/* Amount / Description */}
        {data && (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 mb-6 text-start">
            {data.description && (
              <div className="flex justify-between items-start mb-2 gap-3">
                <span className="text-white/50 text-xs">{isAr ? 'الوصف' : 'Description'}</span>
                <span className="text-white text-sm font-medium text-end">{data.description}</span>
              </div>
            )}
            {data.amount != null && (
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/50 text-xs">{isAr ? 'المبلغ' : 'Amount'}</span>
                <span className="text-[#d4af37] font-bold text-lg">
                  {floorAED(data.amount).toFixed(2)} <span className="text-xs">{data.currency}</span>
                </span>
              </div>
            )}
            {data.customer_name && (
              <div className="flex justify-between items-center">
                <span className="text-white/50 text-xs">{isAr ? 'العميل' : 'Customer'}</span>
                <span className="text-white text-sm">{data.customer_name}</span>
              </div>
            )}
          </div>
        )}

        {/* Session ID (copyable, for support) */}
        <div className="flex items-center justify-center gap-2 mb-6 text-xs text-white/40">
          <span>{isAr ? 'معرف الدفعة:' : 'Payment ID:'}</span>
          <button
            onClick={copySession}
            className="font-mono text-white/70 hover:text-[#d4af37] transition flex items-center gap-1.5"
            data-testid="payment-copy-session-btn"
          >
            {sessionId.slice(0, 12)}…
            <Copy className="w-3 h-3" />
          </button>
          {copied && <span className="text-[#d4af37]">{isAr ? 'تم النسخ' : 'Copied'}</span>}
        </div>

        {/* Retry button for failed/cancelled with an active link */}
        {showRetry && (
          <a
            href={data.payment_link}
            className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 rounded-xl bg-[#d4af37] text-black font-bold hover:bg-[#d4af37]/90 transition mb-3"
            data-testid="payment-retry-btn"
          >
            <RefreshCcw className="w-4 h-4" />
            {isAr ? 'المحاولة مرة أخرى' : 'Try Again'}
          </a>
        )}

        {/* Poll exhausted banner */}
        {pollExhausted && !TERMINAL.has(status) && (
          <div className="mt-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs">
            {isAr
              ? 'لم نستلم تأكيداً نهائياً بعد. سيتم إشعارك بالبريد فور اكتمال الدفعة.'
              : 'No final confirmation received yet. You will be emailed when the payment settles.'}
          </div>
        )}

        <button
          onClick={() => navigate('/')}
          className="mt-3 inline-flex items-center gap-2 w-full justify-center px-6 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08] transition"
          data-testid="payment-home-btn"
        >
          <Home className="w-4 h-4" />
          {isAr ? 'الصفحة الرئيسية' : 'Home'}
        </button>
      </motion.div>
    </div>
  );
}
